/**
 * Timeline Thumbnail Sprite Preview
 *
 * Displays video thumbnails using VTT sprite sheets.
 * Much more efficient than on-demand API calls:
 * - 1 VTT file + 1-3 sprite images (vs 50-100 API calls)
 * - ~200KB total download (vs 250KB-1MB)
 * - Instant display (already loaded vs network latency per thumb)
 *
 * VTT Format Example:
 * ```
 * WEBVTT
 *
 * 1
 * 00:00:00.000 --> 00:00:10.000
 * https://cdn/sprite-00001.jpg#xywh=0,0,160,90
 *
 * 2
 * 00:00:10.000 --> 00:00:20.000
 * https://cdn/sprite-00001.jpg#xywh=160,0,160,90
 * ```
 */

import type { ReactElement } from "react";
import React, { useEffect, useMemo, useRef } from "react";

import { formatTime, type ThumbnailCue, useThumbnailSprite } from "../../../shared/hooks/useThumbnailSprite";

/**
 * Scale bounds for thumbnail sizing
 */
const MIN_SCALE = 0.5;
const MAX_SCALE = 1;

export type TimelineThumbnailSpriteProps = Readonly<{
  /** VTT file URL (from asset.thumbnailVttUrl) */
  vttUrl: string | null;
  /** Current hover time in seconds */
  hoverTime: number | null;
  /** Duration of the video in seconds (unused but kept for API consistency) */
  _duration: number;
  /** Position of the thumbnail (left offset in pixels) */
  leftOffset: number;
  /** Whether to show the thumbnail */
  visible: boolean;
  /** Scale factor for thumbnail size (default: 1.0, range: 0.5-2.0) */
  scale?: number;
}>;

/** Render thumbnail content based on state */
function ThumbnailContent({
  vttError,
  loadingVtt,
  imageUrl,
  coords,
  clampedScale,
  backgroundSize,
  isPreloaded,
  hoverTime,
}: {
  readonly vttError: boolean;
  readonly loadingVtt: boolean;
  readonly imageUrl: string;
  readonly coords: { x: number; y: number };
  readonly clampedScale: number;
  readonly backgroundSize: number;
  readonly isPreloaded: boolean;
  readonly hoverTime: number;
}): ReactElement {
  if (vttError) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-xs text-neutral-500">
        Preview unavailable
      </div>
    );
  }

  if (loadingVtt) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-neutral-900">
        <div className="w-6 h-6 border-2 border-neutral-600 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="w-full h-full"
      style={{
        backgroundImage: `url(${imageUrl})`,
        backgroundPosition: `-${coords.x * clampedScale}px -${coords.y * clampedScale}px`,
        backgroundSize: `${backgroundSize}px auto`,
        backgroundRepeat: "no-repeat",
        // Note: We removed the opacity:0 for non-preloaded images.
        // CSS background-image handles loading gracefully - it shows nothing
        // until loaded, then displays. The preloadedSprites check is now
        // only used for the fade-in transition effect.
        opacity: 1,
        transition: isPreloaded ? "none" : "opacity 300ms",
      }}
      aria-label={`Preview at ${formatTime(hoverTime)}`}
    />
  );
}

export const TimelineThumbnailSprite = React.memo(function TimelineThumbnailSprite({
  vttUrl,
  hoverTime,
  _duration,
  leftOffset,
  visible,
  scale = 1,
}: TimelineThumbnailSpriteProps): ReactElement | null {
  // Use skipPreload to avoid sprite loading during video playback
  // Sprites are preloaded separately via preloadThumbnailSprite() after playback starts
  const {
    loading: loadingVtt,
    error: vttError,
    preloadedSprites,
    spriteColumns,
    findCue,
  } = useThumbnailSprite(vttUrl, "standard", { skipPreload: true });

  const currentCue = useRef<ThumbnailCue | null>(null);

  useEffect(() => {
    if (hoverTime !== null) {
      currentCue.current = findCue(hoverTime);
    }
  }, [hoverTime, findCue]);

  // Clamp scale (must be before conditional returns per Rules of Hooks)
  const clampedScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));

  // Compute scaled dimensions with useMemo (must be before conditional returns)
  const cue = currentCue.current;

  // Memoize based on stable values (index) instead of object reference
  const cueIndex = cue?.index ?? -1;
  const cueWidth = cue?.coords.w ?? 0;
  const cueHeight = cue?.coords.h ?? 0;

  const scaledDimensions = useMemo(() => {
    if (cueIndex === -1) return { width: 0, height: 0, backgroundSize: 0 };
    return {
      width: cueWidth * clampedScale,
      height: cueHeight * clampedScale,
      backgroundSize: cueWidth * spriteColumns * clampedScale,
    };
  }, [cueIndex, cueWidth, cueHeight, clampedScale, spriteColumns]);

  // Don't render if not visible or no data
  if (!visible || !vttUrl || !hoverTime || !cue) {
    return null;
  }

  const { imageUrl, coords } = cue;

  return (
    <div
      className="fixed bottom-40 pointer-events-none z-50"
      style={{
        left: `${leftOffset}px`,
        transform: "translateX(-50%)",
        opacity: visible ? 1 : 0,
        transition: "opacity 0.5s ease-in-out",
      }}
    >
      <div className="bg-black/90 border border-neutral-700 rounded-lg overflow-hidden">
        {/* Sprite Thumbnail */}
        <div
          className="relative bg-neutral-900"
          style={{
            width: scaledDimensions.width,
            height: scaledDimensions.height,
          }}
        >
          <ThumbnailContent
            vttError={vttError}
            loadingVtt={loadingVtt}
            imageUrl={imageUrl}
            coords={coords}
            clampedScale={clampedScale}
            backgroundSize={scaledDimensions.backgroundSize}
            isPreloaded={preloadedSprites.has(imageUrl)}
            hoverTime={hoverTime}
          />
        </div>

        {/* Time Label */}
        <div className="px-2 py-1 text-center">
          <span className="text-xs font-medium text-white tabular-nums">{formatTime(hoverTime)}</span>
        </div>
      </div>

      {/* Arrow pointing to timeline */}
      <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 pr-2" style={{ width: 0, height: 0 }}>
        <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-lumi-accent" />
      </div>
    </div>
  );
});
